import { useFieldArray, useFormContext } from 'react-hook-form';
import { Divider, Grid, styled, Typography } from '@mui/material';
import { LwButton } from 'redesign';
import { IconInfoRedesigned } from 'assets/img';
import { PreferredWorkerOptions } from 'api/services/shift/schema/preferred-worker-options.schema';
import { useMemo } from 'react';
import { getWorkerFullName } from 'shared/utils/worker';
import { PreferredWorkerRule } from './preferred-worker-rule';
import { PreferredWorkerFormContext } from './preferred-worker.types';
import { WorkerOption } from './inputs/preferred-worker-multiselect';

type Props = {
  preferredWorkerOptions: PreferredWorkerOptions | undefined;
  fieldsDisabled?: boolean;
};

export const PreferredWorkerWrapper = ({ preferredWorkerOptions, fieldsDisabled }: Props) => {
  const { control, watch } = useFormContext<PreferredWorkerFormContext>();
  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'visibilityRules',
  });
  const visibilityRules = watch('visibilityRules') || [];
  const allSelectedWorkerOptions = visibilityRules.flatMap((rule) => rule.workerOptions);
  const addHandler = (index: number) => {
    insert(index + 1, { workerOptions: [], durationCondition: null });
  };
  const deleteHandler = (index: number) => {
    remove(index);
  };

  const workerOptionsMapped = useMemo(() => {
    if (!preferredWorkerOptions) {
      return [];
    }
    return mapPreferredWorkerOptions(preferredWorkerOptions);
  }, [preferredWorkerOptions, visibilityRules.length]);

  const workerOptionsWithoutSelected = useMemo(() => {
    if (allSelectedWorkerOptions.length !== 0) {
      return workerOptionsMapped.filter(
        (item) =>
          !visibilityRules.some((rule) =>
            rule.workerOptions.some((option) => option.id === item.id)
          )
      );
    }
    return workerOptionsMapped;
  }, [allSelectedWorkerOptions.length, visibilityRules.length, preferredWorkerOptions]);

  return (
    <Grid container item columnSpacing={4} xs={12}>
      {fields.length > 0 ? (
        fields.map((field, index) => (
          <>
            <PreferredWorkerRule
              key={field.id}
              index={index}
              workerOptions={workerOptionsWithoutSelected}
              handleDelete={deleteHandler}
              handleNewRule={addHandler}
              disabled={fieldsDisabled}
            />
            {index < fields.length - 1 ? (
              <StyledDividerContainer item xs={12}>
                <Divider />
              </StyledDividerContainer>
            ) : null}
          </>
        ))
      ) : (
        <Grid item>
          <LwButton
            color="secondary"
            onClick={() => append({ workerOptions: [], durationCondition: null })}
            disabled={fieldsDisabled}
          >
            Voorwaarde toevoegen
          </LwButton>
        </Grid>
      )}

      <StyledDividerContainer item xs={12}>
        <Divider />
      </StyledDividerContainer>

      <Grid item container flexDirection={'row'}>
        <Grid container item xs={12} flexDirection={'row'}>
          <Grid item xs={1} columnSpacing={0}>
            <IconInfoRedesigned />
          </Grid>
          <Grid item xs={11}>
            <Typography variant="subtitle1">
              Deze shift wordt voor de hele pool beschikbaar zodra de ingestelde reactietermijn is
              verstreken of alle voorkeursflexwerkers zijn geaccepteerd of afgewezen.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapPreferredWorkerOptions = (
  preferredWorkerOptions: PreferredWorkerOptions
): WorkerOption[] => {
  return preferredWorkerOptions.flatMap((partner) => {
    return partner.partnerWorkers.map((worker) => ({
      id: worker.workerId,
      name: getWorkerFullName({
        firstName: worker.firstName,
        lastName: worker.lastName,
        infix: worker.infix,
      }),
      description: partner.partnerName,
    }));
  });
};

const StyledDividerContainer = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));
